
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import IconFilter from "@/components/common/icon/IconFilter.vue";
import Multiselect from "vue-multiselect";
import { mapGetters } from "vuex";
import { CashOrderStatusEnum } from "@/entities";

export interface IApprovalsFilterModel {
  status?: CashOrderStatusEnum[];
  username?: string;
  serviceCode?: string;
  orderDateFrom: Date;
  orderDateTo: Date;
}

@Component({
  name: "approvals-filter",
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    IconFilter,
    Multiselect,
  },
})
export default class ApprovalsFilter extends Vue {
  @Prop({ required: true }) params!: IApprovalsFilterModel;

  visible = false;
  paramsEdit: IApprovalsFilterModel = { ...this.params };

  private static readonly MAX_RANGE_DAYS = 14;

  get locale(): string {
    return (this as any).getLocale || "en";
  }

  get statusOptions(): Array<{ code: CashOrderStatusEnum; desc: string }> {
    return [
      { code: CashOrderStatusEnum.PENDING_APPROVAL, desc: this.$t("label.pending_approval") },
      { code: CashOrderStatusEnum.REJECTED_APPROVAL, desc: this.$t("label.rejected_approval") },
      { code: CashOrderStatusEnum.APPROVED_APPROVAL, desc: this.$t("label.approved_approval") },
    ];
  }

  get statusSelected(): Array<{ code: CashOrderStatusEnum; desc: string }> {
    const selected = new Set(this.paramsEdit.status || []);
    return this.statusOptions.filter((o) => selected.has(o.code));
  }

  set statusSelected(value: Array<{ code: CashOrderStatusEnum; desc: string }>) {
    this.paramsEdit.status = (value || []).map((v) => v.code);
  }

  get orderDateFrom(): string {
    return this.paramsEdit.orderDateFrom ? this.toYYYYMMDD(this.paramsEdit.orderDateFrom) : "";
  }

  set orderDateFrom(value: string) {
    this.paramsEdit.orderDateFrom = value
      ? new Date(`${value}T00:00:00`)
      : this.paramsEdit.orderDateFrom;
  }

  get orderDateTo(): string {
    return this.paramsEdit.orderDateTo ? this.toYYYYMMDD(this.paramsEdit.orderDateTo) : "";
  }

  set orderDateTo(value: string) {
    this.paramsEdit.orderDateTo = value
      ? new Date(`${value}T00:00:00`)
      : this.paramsEdit.orderDateTo;
  }

  get filterLength(): number {
    let length = 0;
    if (this.params.status?.length) length++;
    if (this.params.username) length++;
    if (this.params.serviceCode) length++;
    if (this.params.orderDateFrom) length++;
    if (this.params.orderDateTo) length++;
    return length;
  }

  get orderDateFromMax(): string {
    return this.orderDateTo || "";
  }

  get orderDateToMin(): string {
    return this.orderDateFrom || "";
  }

  get orderDateToMax(): string {
    const from = this.startOfDay(this.paramsEdit.orderDateFrom);
    const max = this.addDays(from, ApprovalsFilter.MAX_RANGE_DAYS);
    return this.toYYYYMMDD(max);
  }

  @Watch("paramsEdit.orderDateFrom", { deep: false })
  onOrderDateFromChanged(newVal: Date): void {
    if (!newVal) return;

    const from = this.startOfDay(newVal);
    if (this.paramsEdit.orderDateFrom.getTime() !== from.getTime()) {
      this.paramsEdit.orderDateFrom = from;
    }

    const maxToAllowed = this.addDays(from, ApprovalsFilter.MAX_RANGE_DAYS);

    const currentTo = this.startOfDay(this.paramsEdit.orderDateTo);

    if (currentTo.getTime() < from.getTime()) {
      this.paramsEdit.orderDateTo = new Date(from);
      return;
    }

    if (currentTo.getTime() > maxToAllowed.getTime()) {
      this.paramsEdit.orderDateTo = new Date(maxToAllowed);
    }
  }

  @Watch("paramsEdit.orderDateTo", { deep: false })
  onOrderDateToChanged(newVal: Date): void {
    if (!newVal) return;

    const to = this.startOfDay(newVal);
    if (this.paramsEdit.orderDateTo.getTime() !== to.getTime()) {
      this.paramsEdit.orderDateTo = to;
    }

    const from = this.startOfDay(this.paramsEdit.orderDateFrom);
    const maxToAllowed = this.addDays(from, ApprovalsFilter.MAX_RANGE_DAYS);

    if (to.getTime() < from.getTime()) {
      this.paramsEdit.orderDateTo = new Date(from);
      return;
    }

    if (to.getTime() > maxToAllowed.getTime()) {
      this.paramsEdit.orderDateTo = new Date(maxToAllowed);
    }
  }

  private startOfDay(d: Date): Date {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private addDays(d: Date, days: number): Date {
    const date = new Date(d);
    date.setDate(date.getDate() + days);
    return date;
  }

  doCleanUp(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = new Date(today);
    from.setDate(from.getDate() - 7);

    this.paramsEdit = {
      status: undefined,
      username: undefined,
      orderDateFrom: from,
      orderDateTo: today,
    };

    this.$emit("update:params", { ...this.paramsEdit });
    this.visible = false;
    this.$emit("filter");
  }

  doFilter(): void {
    this.$emit("update:params", { ...this.paramsEdit });
    this.visible = false;
    this.$emit("filter");
  }

  private toYYYYMMDD(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
}
